import React, { useMemo } from "react";
import {
    useTheme,
    useMediaQuery,
    Box,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
    Stack,
} from "@mui/material";
import { Place } from "@mui/icons-material";
import {
    OTP_DELAY_MINUTES,
    DIVERT_RETURN_STATUS,
} from "state/constants";
import { countOTPOverall } from '../otpAnalytics/utils';
import StatBar from 'components/StatBar';
import PieChart from 'components/chartNivo/PieChart';


const OtpByPorts = ({
    flights = [],
    delayType,
    onDelayTypeChange,
    selectedPorts,
    onSelectedPortsChange,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const ports = useMemo(() => {
        if (!Array.isArray(flights)) return [];
        return Array.from(new Set(
            flights.flatMap(f => [f.dep, f.arr])
        )).filter(Boolean).sort();
    }, [flights]);

    const flightsByPort = useMemo(() => {
        if (!Array.isArray(flights)) return [];

        const field = delayType === "departure" ? "dep" : "arr";

        return flights.filter(f => {
            if (!f) return false;

            // Filter selected port
            if (selectedPorts.length && !selectedPorts.includes(f[field])) {
                return false;
            }

            if (delayType === "departure") {
                return f.flightStatus !== "Not yet departed";
            }

            return f.flightStatus === "Arrived";
        });
    }, [flights, selectedPorts, delayType]);

    const chartData = useMemo(() => {
        return countOTPOverall(
            flightsByPort,
            delayType,
            "pie",
            OTP_DELAY_MINUTES
        );
    }, [flightsByPort, delayType]);

    const stackData = useMemo(() => {
        if (!Array.isArray(flightsByPort)) return [];

        const field = delayType === "departure" ? "dep" : "arr";
        const delayField = delayType === "departure"
            ? "depDelaysTime"
            : "arrDelaysTime";

        const map = flightsByPort.reduce((acc, f) => {
            if (!f || !f[field]) return acc;

            const key = f[field];

            if (!acc[key]) {
                acc[key] = {
                    key,
                    label: key,
                    total: 0,
                    delay: 0,
                    ontime: 0,
                };
            }

            acc[key].total++;

            const isDelay =
                !DIVERT_RETURN_STATUS.includes(f.flightStatus) &&
                f[delayField] > OTP_DELAY_MINUTES;

            if (isDelay)
                acc[key].delay++
            else
                acc[key].ontime++;

            return acc;
        }, {});

        return Object.values(map)
            .map(item => {
                const percent = item.total === 0
                    ? 0
                    : (item.ontime / item.total) * 100;

                return {
                    key: item.key,
                    label: item.label,
                    count: `On time ${item.ontime}/ ${item.total}`,
                    percent: percent.toFixed(1),
                    targetPercent: 90,
                };
            })
            .sort((a, b) => a.percent - b.percent);
    }, [flightsByPort, delayType]);

    const getColorFromKey = (key) => {
        let hash = 0;
        for (let i = 0; i < key.length; i++) {
            hash = key.charCodeAt(i) + ((hash << 5) - hash);
        }

        const GOLDEN_ANGLE = 137.508;
        const hue = (key * GOLDEN_ANGLE) % 360;
        const saturation = 60 + (Math.abs(hash) % 20);
        const lightness = 50 + (Math.abs(hash >> 3) % 15);

        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };


    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                overflowX: 'hidden',
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                p={1}
            >
                <FormControl
                    size='small'
                    sx={{ minWidth: '6rem' }}
                >
                    <InputLabel>Delay Type</InputLabel>
                    <Select
                        label="Delay Type"
                        value={delayType}
                        onChange={(e) => onDelayTypeChange(e.target.value)}
                    >
                        <MenuItem value="departure">Departure</MenuItem>
                        <MenuItem value="arrival">Arrival</MenuItem>
                    </Select>
                </FormControl>

                <FormControl
                    size="small"
                    sx={{ minWidth: '5rem' }}
                >
                    <InputLabel>Port</InputLabel>
                    <Select
                        multiple={true}
                        label="Port"
                        name="selectedPorts"
                        value={selectedPorts}
                        onChange={(e) => onSelectedPortsChange(e.target.value)}
                    >
                        {ports.map((portCode, index) => (
                            <MenuItem key={index} value={portCode}>
                                {portCode}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box
                flex={1}
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                gap={1}
            >
                <Box sx={{ height: '98%', width: isMobile ? "100%" : '50%' }}>
                    <PieChart
                        data={chartData}
                        itemWidthOffSet={-20}
                    />
                </Box>

                <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ minHeight: '40%', width: isMobile ? "100%" : '50%' }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: stackData.length < 6 ? 'center' : 'flex-start',
                            alignItems: 'center',
                            overflowY: 'auto',
                            py: 1,
                            px: 5,
                        }}
                    >
                        <Stack
                            spacing={1}
                            sx={{ width: '100%' }}
                        >
                            {stackData?.map((f, index) => (
                                <StatBar
                                    key={f.key}
                                    icon={<Place fontSize="small"/>}
                                    label={f.label}
                                    count={f.count}
                                    percent={f.percent}
                                    targetPercent={f.targetPercent}
                                    color={getColorFromKey(index)}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default OtpByPorts;
